import React, { useState } from 'react';
import axios from 'axios';
import { Lock, Plus, X, Send } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { TicketStatusBadge, TicketPriorityBadge } from './StatusBadge';
import { AttachmentUploader, renderAttachmentPreview } from './AttachmentUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function CustomerTicketDetail({ ticket, token, onClose, onUpdated }) {
  const [replyBody, setReplyBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [editingFields, setEditingFields] = useState(false);
  const [privateFields, setPrivateFields] = useState(
    (ticket?.private_fields || []).map((f) => ({ label: f.label, value: '' }))
  );

  if (!ticket) return null;
  const headers = { Authorization: `Bearer ${token}` };

  const sendReply = async () => {
    if (!replyBody.trim() && attachments.length === 0) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/portal/tickets/${ticket.id}/messages`, {
        body: replyBody.trim(),
        attachments,
      }, { headers });
      setReplyBody('');
      setAttachments([]);
      onUpdated();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const addPrivateField = () => setPrivateFields((prev) => [...prev, { label: '', value: '' }]);
  const updateField = (idx, key, val) => setPrivateFields((prev) => prev.map((f, i) => (i === idx ? { ...f, [key]: val } : f)));
  const removeField = (idx) => setPrivateFields((prev) => prev.filter((_, i) => i !== idx));

  const savePrivateFields = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/portal/tickets/${ticket.id}/private-fields`,
        privateFields.filter((f) => f.label.trim()),
        { headers }
      );
      setEditingFields(false);
      onUpdated();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update fields');
    }
  };

  return (
    <Sheet open={!!ticket} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="customer-ticket-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            <span>#{ticket.ticket_number} &mdash; {ticket.subject}</span>
            <TicketStatusBadge status={ticket.status} />
            <TicketPriorityBadge priority={ticket.priority} />
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {ticket.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {ticket.tags.map((t) => (
                <span key={t} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t}</span>
              ))}
            </div>
          )}

          {/* Private fields */}
          <div className="border rounded-lg p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5 text-slate-400" /> Private access fields
              </span>
              <button
                className="text-xs font-medium text-[#6e2ea8]"
                onClick={() => setEditingFields((v) => !v)}
                data-testid="toggle-edit-private-fields"
                disabled={ticket.status === 'archived'}
              >
                {editingFields ? 'Cancel' : 'Update'}
              </button>
            </div>
            {!editingFields ? (
              (ticket.private_fields || []).length === 0 ? (
                <p className="text-xs text-slate-500">None submitted.</p>
              ) : (
                <ul className="text-xs text-slate-600 space-y-1">
                  {ticket.private_fields.map((f) => (
                    <li key={f.id}>{f.label}: <span className="italic text-slate-400">submitted &bull; visible to support team only</span></li>
                  ))}
                </ul>
              )
            ) : (
              <div className="space-y-2">
                {ticket.status === 'archived' && (
                  <p className="text-xs text-amber-600">Reply below to reopen this ticket before updating fields.</p>
                )}
                {privateFields.map((f, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input placeholder="Label" value={f.label} onChange={(e) => updateField(idx, 'label', e.target.value)} data-testid={`edit-field-label-${idx}`} />
                    <Input placeholder="New value" type="password" value={f.value} onChange={(e) => updateField(idx, 'value', e.target.value)} data-testid={`edit-field-value-${idx}`} />
                    <button type="button" onClick={() => removeField(idx)}><X className="w-4 h-4 text-slate-400" /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <button type="button" onClick={addPrivateField} className="text-xs text-[#6e2ea8] flex items-center gap-1" data-testid="add-edit-field-btn">
                    <Plus className="w-3.5 h-3.5" /> Add field
                  </button>
                  <Button size="sm" onClick={savePrivateFields} data-testid="save-private-fields-btn">Save</Button>
                </div>
              </div>
            )}
          </div>

          {/* Message thread */}
          <div className="space-y-3">
            {(ticket.messages || []).map((m) => (
              <div key={m.id} className={`p-3 rounded-xl ${m.sender_type === 'customer' ? 'bg-[#6e2ea8]/5 ml-6' : 'bg-slate-100 mr-6'}`} data-testid={`ticket-message-${m.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-700">{m.sender_name || (m.sender_type === 'admin' ? 'Support Team' : 'You')}</span>
                  <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                {(m.attachments || []).map((a, i) => renderAttachmentPreview(a, i))}
                {m.body && <p className="text-sm text-slate-700 whitespace-pre-wrap">{m.body}</p>}
              </div>
            ))}
          </div>

          {/* Reply box */}
          {ticket.status !== 'archived' ? (
            <div className="border-t pt-4">
              <Textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Type a reply or paste a screenshot..."
                rows={3}
                data-testid="ticket-reply-input"
              />
              <div className="flex items-center justify-between mt-2">
                <AttachmentUploader
                  uploadEndpoint="/api/portal/tickets/attachments"
                  token={token}
                  attachments={attachments}
                  setAttachments={setAttachments}
                  testIdPrefix="ticket-reply"
                />
                <Button size="sm" onClick={sendReply} disabled={sending} className="bg-[#6e2ea8] hover:bg-[#5a2690]" data-testid="send-ticket-reply-btn">
                  <Send className="w-4 h-4 mr-1" /> {sending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="border-t pt-4">
              <p className="text-sm text-slate-500 mb-2">This ticket is archived. Reply below to reopen it.</p>
              <Textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="Reopen with a reply..." rows={3} data-testid="ticket-reopen-input" />
              <Button size="sm" onClick={sendReply} disabled={sending} className="mt-2 bg-[#6e2ea8] hover:bg-[#5a2690]" data-testid="reopen-ticket-btn">
                {sending ? 'Sending...' : 'Reopen Ticket'}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
